import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Camera } from '@capacitor/camera';
import { Browser } from '@capacitor/browser';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { App as CapacitorApp } from '@capacitor/app';
import { JigsawGame } from './game/JigsawGame';
import { PhotoManager } from './utils/PhotoManager';
import { ImageCompressor } from './utils/ImageCompressor';
import { ShareManager } from './utils/ShareManager';
import { DailyPuzzleManager } from './utils/DailyPuzzleManager';
import { getDailyPuzzleByDate, getTodaysPuzzle, DailyPuzzle } from './data/dailyPuzzles';
import { getLevelPuzzleByNum, LEVELS_PER_PAGE } from './data/levelPuzzles';
import { CollectionManager, PuzzleCollection } from './utils/CollectionManager';
import { dataUrlToBlob } from './utils/BlobUtils';
import { BASE_URL } from './config';

// DEBUG FLAG: Set to true to force all puzzles to 3x3 grid
const FORCE_3X3_GRID = false;

// Puzzle data embedded server-side to avoid a REST round-trip on page load
interface EmbeddedPuzzleData {
  imageUrl: string;
  difficulty: number;
  seed: number;
  id: string;
  type: 'daily' | 'shared';
}

// Platform type: 'web' or 'app'
const PLATFORM_TYPE = Capacitor.isNativePlatform() ? 'app' : 'web';

class App {
  private game: JigsawGame | null = null;
  private currentCalendarMonth: number = new Date().getMonth() + 1; // Default to current month
  private currentCalendarYear: number = new Date().getFullYear(); // Default to current year
  private currentCollectionId: string | null = null;
  private currentPuzzleIndex: number = -1;
  private currentPuzzleServerId: string | null = null; // Server ID for shareable puzzles
  private currentDailyPuzzleDate: string | null = null; // Date for daily puzzles (yyyy-mm-dd)
  private currentLevelNum: number | null = null; // Current level number
  private currentLevelPage: number = 0; // 0 = levels 1-20, 1 = levels 21-40, etc.

  // Helper method to add both click and touch events for iOS compatibility
  private addClickHandler(element: HTMLElement, handler: (e?: Event) => void) {
    element.addEventListener('click', handler);

    // Track touch position to distinguish tap from scroll
    let touchStartY = 0;
    let touchStartX = 0;
    let touchStartTime = 0;

    element.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
      touchStartTime = Date.now();
    }, { passive: true });

    element.addEventListener('touchend', (e) => {
      const touchEndY = e.changedTouches[0].clientY;
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndTime = Date.now();

      // Calculate distance moved
      const deltaY = Math.abs(touchEndY - touchStartY);
      const deltaX = Math.abs(touchEndX - touchStartX);
      const deltaTime = touchEndTime - touchStartTime;

      // Only trigger if it's a tap (not a scroll)
      // Tap = moved less than 10px and took less than 500ms
      if (deltaY < 10 && deltaX < 10 && deltaTime < 500) {
        e.preventDefault();
        handler(e);
      }
    });
  }

  private addSwipeHandler(element: HTMLElement, onSwipeLeft: () => void, onSwipeRight: () => void) {
    let startX = 0;
    let startY = 0;

    element.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    element.addEventListener('touchend', (e) => {
      const deltaX = e.changedTouches[0].clientX - startX;
      const deltaY = Math.abs(e.changedTouches[0].clientY - startY);
      // Require at least 60px horizontal and more horizontal than vertical
      if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > deltaY) {
        if (deltaX < 0) {
          onSwipeLeft();
        } else {
          onSwipeRight();
        }
      }
    }, { passive: true });
  }

  private animateSlide(element: HTMLElement, direction: 'left' | 'right', updateFn: () => void) {
    const outX = direction === 'left' ? '-100%' : '100%';
    const inX  = direction === 'left' ? '100%'  : '-100%';

    element.style.transition = 'transform 0.2s ease-in';
    element.style.transform  = `translateX(${outX})`;

    setTimeout(() => {
      element.style.transition = 'none';
      element.style.transform  = `translateX(${inX})`;
      updateFn();
      void element.offsetHeight; // force reflow before re-enabling transition
      element.style.transition = 'transform 0.2s ease-out';
      element.style.transform  = 'translateX(0)';
    }, 200);
  }

  async initialize() {
    const splashStart = Date.now();
    console.log('Initializing JoinSaw...');

    // Add native class to body for platform-specific styling
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add('native');

      await StatusBar.setStyle({ style: Style.Dark });
      // setBackgroundColor is only available on Android
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({ color: '#1a1a1a' });
      }

      // Setup deep link listener (app already running in background)
      CapacitorApp.addListener('appUrlOpen', (data) => {
        console.log('App opened with URL:', data.url);
        this.handleDeepLink(data.url);
      });

      // Handle cold launch via deep link (app not running)
      CapacitorApp.getLaunchUrl().then((result) => {
        if (result?.url) {
          console.log('App launched with URL:', result.url);
          this.handleDeepLink(result.url);
        }
      });
    }

    // Initialize game canvas but don't start yet
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (canvas) {
      this.game = new JigsawGame(canvas);
    }

    this.setupControls();
    this.setupFrontPageControls();

    // Check if a daily puzzle date was specified (from window variable, URL path, or URL parameter)
    let dailyDate = (window as any).DAILY_PUZZLE_DATE;
    if (!dailyDate) {
      // Check for path-based URL: /daily/2025-12-26
      const pathMatch = window.location.pathname.match(/\/daily\/(\d{4}-\d{2}-\d{2})/);
      if (pathMatch) {
        dailyDate = pathMatch[1];
      } else {
        // Fallback to query parameter: ?daily=2025-12-26
        const urlParams = new URLSearchParams(window.location.search);
        dailyDate = urlParams.get('daily');
      }
    }

    const sharedPuzzleId = (window as any).SHARED_PUZZLE_ID as string | undefined;

    const embeddedPuzzle = (window as any).PUZZLE_DATA as EmbeddedPuzzleData | undefined;

    if (dailyDate && dailyDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      console.log('Auto-loading daily puzzle:', dailyDate);
      if (embeddedPuzzle?.type === 'daily') {
        await this.loadEmbeddedPuzzle(embeddedPuzzle, dailyDate, null);
      } else {
        await this.loadDailyPuzzle(dailyDate);
      }
    } else if (sharedPuzzleId) {
      console.log('Auto-loading shared puzzle:', sharedPuzzleId);
      if (embeddedPuzzle?.type === 'shared') {
        await this.loadEmbeddedPuzzle(embeddedPuzzle, null, sharedPuzzleId);
      } else {
        await this.loadSharedPuzzleFromServer(sharedPuzzleId);
      }
    } else {
      // Show front page initially only if no puzzle is being loaded
      this.showFrontPage();
      this.preloadFrontPageImages();
    }

    const loading = document.getElementById('loading');
    if (loading) {
      loading.style.display = 'none';
    }

    console.log('JoinSaw initialized successfully');

    // Dismiss splash screen (minimum 1s display so it doesn't just flash)
    const elapsed = Date.now() - splashStart;
    const delay = Math.max(0, 3000 - elapsed);
    setTimeout(() => {
      const splash = document.getElementById('splash-screen');
      if (splash) {
        splash.classList.add('fade-out');
        setTimeout(() => splash.remove(), 500);
      }
    }, delay);
  }

  private setupControls() {
    // Footer controls (for native platforms)
    const newGameBtnFooter = document.getElementById('new-game-footer');
    const pickPhotoBtnFooter = document.getElementById('pick-photo-footer');
    const gridSizeSelectFooter = document.getElementById('grid-size-footer') as HTMLSelectElement;

    // Setup footer controls (native)
    if (gridSizeSelectFooter) {
      gridSizeSelectFooter.addEventListener('change', () => {
        const size = parseInt(gridSizeSelectFooter.value);
        console.log('Grid size changed to', size);
        this.game?.setGridSize(size);
        this.game?.startNewGame();
      });
    }

    if (newGameBtnFooter) {
      newGameBtnFooter.addEventListener('click', async () => {
        console.log('New game clicked');
        if (gridSizeSelectFooter) {
          const size = parseInt(gridSizeSelectFooter.value);
          this.game?.setGridSize(size);
        }
        await this.startNewGameWithSavedPhotos();
      });
    }

    if (pickPhotoBtnFooter) {
      pickPhotoBtnFooter.addEventListener('click', () => {
        this.showPhotoModal();
      });
    }

    // Setup share buttons
    const shareBtn = document.getElementById('share-btn');
    const shareBtnFooter = document.getElementById('share-btn-footer');

    if (shareBtn) {
      this.addClickHandler(shareBtn as HTMLElement, () => {
        this.showShareModal();
      });
    }

    if (shareBtnFooter) {
      this.addClickHandler(shareBtnFooter as HTMLElement, () => {
        this.showShareModal();
      });
    }

    // Setup modal controls
    const closeModalBtn = document.getElementById('close-modal');
    const pickNewPhotoBtn = document.getElementById('pick-new-photo');

    if (closeModalBtn) {
      this.addClickHandler(closeModalBtn as HTMLElement, () => {
        this.hidePhotoModal();
      });
    }

    if (pickNewPhotoBtn) {
      this.addClickHandler(pickNewPhotoBtn as HTMLElement, async () => {
        await this.pickPhoto();
      });
    }

    // Setup share modal controls
    const closeShareModalBtn = document.getElementById('close-share-modal');
    if (closeShareModalBtn) {
      this.addClickHandler(closeShareModalBtn as HTMLElement, () => {
        this.hideShareModal();
      });
    }

    const shareOptions = document.querySelectorAll('.share-option');
    shareOptions.forEach(btn => {
      this.addClickHandler(btn as HTMLElement, async (e) => {
        const target = e?.currentTarget as HTMLElement;
        const shareType = target.getAttribute('data-share-type');
        await this.handleShare(shareType);
      });
    });
  }

  private async startNewGameWithSavedPhotos() {
    // Get a random image from default images or user photos
    const imageUrl = PhotoManager.getRandomImageUrl();
    await this.game?.startNewGame(true, imageUrl);
  }

  private showPhotoModal() {
    const modal = document.getElementById('photo-modal');
    if (modal) {
      modal.classList.add('show');
      this.renderPhotoGallery();
    }
  }

  private hidePhotoModal() {
    const modal = document.getElementById('photo-modal');
    if (modal) {
      modal.classList.remove('show');
    }
  }

  private async showShareModal() {
    const modal = document.getElementById('share-modal');
    const urlDisplay = document.getElementById('share-url-display');
    const copyMessage = document.getElementById('copy-message');
    const copyIcon = document.getElementById('copy-icon');
    const thumbnail = document.getElementById('share-thumbnail') as HTMLImageElement;

    if (modal && urlDisplay) {
      // Reset copy message and icon
      if (copyMessage) {
        copyMessage.style.opacity = '0';
      }
      if (copyIcon) {
        copyIcon.textContent = '📋';
      }

      // Set the thumbnail image
      if (thumbnail && this.game) {
        const imageDataUrl = this.game.getCurrentImageDataUrl();
        if (imageDataUrl) {
          thumbnail.src = imageDataUrl;
          thumbnail.style.display = 'block';
        } else {
          thumbnail.style.display = 'none';
        }
      }

      // Generate the share URL
      const shareUrl = await ShareManager.generateShareUrl(this.currentPuzzleServerId, this.currentDailyPuzzleDate || undefined);

      // Display the URL (or message if not shareable)
      urlDisplay.textContent = shareUrl;
      urlDisplay.style.display = 'block';

      // Show the modal
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideShareModal() {
    const modal = document.getElementById('share-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
  }

  private async handleShare(shareType: string | null) {
    const urlDisplay = document.getElementById('share-url-display');
    const shareUrl = urlDisplay?.textContent || '';

    if (!shareUrl) {
      console.error('No share URL available');
      return;
    }

    try {
      switch (shareType) {
        case 'copy':
          const copied = await ShareManager.copyToClipboard(shareUrl);
          if (copied) {
            // Show success message
            const copyMessage = document.getElementById('copy-message');
            const copyIcon = document.getElementById('copy-icon');

            if (copyMessage) {
              copyMessage.style.opacity = '1';

              // Fade out after 2 seconds
              setTimeout(() => {
                copyMessage.style.opacity = '0';
              }, 2000);
            }

            // Add checkmark to button
            if (copyIcon) {
              copyIcon.textContent = '✓';

              // Reset icon after 3 seconds
              setTimeout(() => {
                copyIcon.textContent = '📋';
              }, 3000);
            }
          } else {
            alert('Failed to copy link. Please try again.');
          }
          break;
        case 'sms':
          await ShareManager.shareViaSMS(shareUrl);
          break;
        case 'email':
          await ShareManager.shareViaEmail(shareUrl);
          break;
        case 'facebook':
          await ShareManager.shareViaFacebook(shareUrl);
          break;
        default:
          console.warn('Unknown share type:', shareType);
      }

      // Close the modal after sharing (except for copy)
      if (shareType !== 'copy') {
        this.hideShareModal();
      }
    } catch (error) {
      console.error('Error sharing:', error);
      alert('Failed to share. Please try again.');
    }
  }

  private async renderPhotoGallery() {
    const gallery = document.getElementById('photo-gallery');
    const emptyMessage = document.getElementById('photo-empty');
    if (!gallery || !emptyMessage) return;

    const photos = PhotoManager.getPhotos();

    if (photos.length === 0) {
      gallery.innerHTML = '';
      emptyMessage.style.display = 'block';
      return;
    }

    emptyMessage.style.display = 'none';

    // Create photo grid with thumbnails
    gallery.innerHTML = photos.map(photo => `
      <div class="photo-item" data-photo-id="${photo.id}">
        <img src="${photo.dataUrl}" data-photo-id="${photo.id}" alt="Photo" onerror="this.parentElement.style.display='none';">
        <button class="photo-item-delete" data-photo-id="${photo.id}">×</button>
      </div>
    `).join('');

    // Clean up photos with broken images after a short delay
    setTimeout(() => {
      const brokenPhotos: string[] = [];
      gallery.querySelectorAll('.photo-item').forEach(item => {
        if ((item as HTMLElement).style.display === 'none') {
          const photoId = item.getAttribute('data-photo-id');
          if (photoId) brokenPhotos.push(photoId);
        }
      });

      // Remove broken photos from storage
      if (brokenPhotos.length > 0) {
        console.log('Removing broken photo references:', brokenPhotos.length);
        brokenPhotos.forEach(id => PhotoManager.deletePhoto(id));
      }
    }, 1000);

    // Add click handlers for photo selection
    const photoItems = gallery.querySelectorAll('.photo-item');
    photoItems.forEach(item => {
      const photoElement = item as HTMLElement;

      photoElement.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement;
        // Don't trigger if clicking delete button
        if (target.classList.contains('photo-item-delete')) return;

        const photoId = photoElement.getAttribute('data-photo-id');
        const photo = photos.find(p => p.id === photoId);
        if (photo && photo.dataUrl) {
          // Show game view and load the custom image
          this.showGameView();
          this.hidePhotoModal();

          // Show loading indicator
          const loading = document.getElementById('loading');
          if (loading) {
            loading.style.display = 'block';
          }

          // Clear any daily puzzle tracking
          DailyPuzzleManager.clearCurrentPuzzle();

          await this.game?.loadCustomImage(photo.dataUrl);

          // Hide loading indicator
          if (loading) {
            loading.style.display = 'none';
          }

          // Hide next puzzle button for custom photos
          this.updateNextPuzzleButton(false);
        }
      });

      // Add long press for mobile to show delete button
      if (Capacitor.isNativePlatform()) {
        let longPressTimer: number | null = null;
        let touchStartX = 0;
        let touchStartY = 0;
        const deleteBtn = photoElement.querySelector('.photo-item-delete') as HTMLElement;

        photoElement.addEventListener('touchstart', (e) => {
          const touchEvent = e as TouchEvent;
          touchStartX = touchEvent.touches[0].clientX;
          touchStartY = touchEvent.touches[0].clientY;

          // Start long press timer (500ms)
          longPressTimer = window.setTimeout(() => {
            // Show delete button on long press
            if (deleteBtn) {
              deleteBtn.style.opacity = '1';
              // Add slight vibration feedback if available
              if ('vibrate' in navigator) {
                navigator.vibrate(50);
              }
            }
          }, 500);
        }, { passive: true });

        photoElement.addEventListener('touchmove', (e) => {
          // Cancel long press if finger moves too much
          const touchEvent = e as TouchEvent;
          const touchX = touchEvent.touches[0].clientX;
          const touchY = touchEvent.touches[0].clientY;
          const deltaX = Math.abs(touchX - touchStartX);
          const deltaY = Math.abs(touchY - touchStartY);

          if (deltaX > 10 || deltaY > 10) {
            if (longPressTimer !== null) {
              window.clearTimeout(longPressTimer);
              longPressTimer = null;
            }
          }
        }, { passive: true });

        photoElement.addEventListener('touchend', () => {
          // Cancel long press timer if touch ends before timeout
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
        });

        photoElement.addEventListener('touchcancel', () => {
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
        });
      }
    });

    // Add delete handlers
    const deleteButtons = gallery.querySelectorAll('.photo-item-delete');
    deleteButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const photoId = btn.getAttribute('data-photo-id');
        if (photoId && confirm('Delete this photo?')) {
          PhotoManager.deletePhoto(photoId);
          this.renderPhotoGallery();
        }
      });
    });

    // On mobile, hide all delete buttons when tapping on the gallery background
    if (Capacitor.isNativePlatform() && gallery) {
      gallery.addEventListener('touchstart', (e) => {
        // Only hide if tapping the gallery itself, not a photo item or delete button
        const target = e.target as HTMLElement;
        if (target.id === 'photo-gallery') {
          const allDeleteBtns = gallery.querySelectorAll('.photo-item-delete') as NodeListOf<HTMLElement>;
          allDeleteBtns.forEach(btn => {
            btn.style.opacity = '0';
          });
        }
      }, { passive: true });
    }

    // Scroll to top to show newly added photos
    gallery.scrollTop = 0;
  }

  private async pickPhoto() {
    console.log('Pick photo clicked, platform:', Capacitor.getPlatform());

    // Use native picker on mobile, file input on web
    if (Capacitor.isNativePlatform()) {
      try {
        const images = await Camera.pickImages({
          quality: 90,
          limit: 10  // Max 10 images per selection
        });

        let processedCount = 0;
        for (const image of images.photos) {
          // Use the path from the photo library directly - no need to copy
          // On iOS: file:/// URI pointing to photo library
          // On Android: content:// URI pointing to MediaStore
          const photoPath = image.path || image.webPath;
          if (photoPath) {
            const saved = PhotoManager.addPhoto(photoPath);
            if (saved !== null) {
              processedCount++;
            }
          }
        }

        if (processedCount === 0) {
          alert('Storage is full! Please delete some photos to add new ones.');
        } else if (processedCount < images.photos.length) {
          alert(`Added ${processedCount} of ${images.photos.length} photos. Storage may be full.`);
        }
        // All photos added successfully - no alert needed

        this.renderPhotoGallery();  // Single refresh after batch
      } catch (error) {
        console.error('Error picking photo', error);
      }
    } else {
      // Browser fallback - use file input with multiple selection
      console.log('Using browser file input');
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true; // Allow multiple file selection

      input.onchange = async (e) => {
        const files = (e.target as HTMLInputElement).files;
        if (files && files.length > 0) {
          console.log(`${files.length} file(s) selected`);

          let processedCount = 0;
          let failedCount = 0;

          // Read all files
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            console.log('Processing file:', file.name);

            const reader = new FileReader();
            reader.onload = async (event) => {
              const dataUrl = event.target?.result as string;
              if (dataUrl) {
                // Compress image before saving
                const compressedDataUrl = await ImageCompressor.compress(dataUrl, 1600, 1600, 0.9);
                // Save to photo library
                const saved = PhotoManager.addPhoto(compressedDataUrl);
                if (saved === null) {
                  failedCount++;
                }
              }

              processedCount++;

              // When all files are processed, refresh the gallery and show errors if any
              if (processedCount === files.length) {
                const successCount = files.length - failedCount;
                console.log(`${successCount} of ${files.length} photo(s) added to library`);

                if (failedCount > 0) {
                  alert(`Storage is full! ${failedCount} photo(s) could not be added. Please delete some photos to free up space.`);
                }

                // Refresh gallery to show all new photos that were successfully added
                this.renderPhotoGallery();
              }
            };
            reader.readAsDataURL(file);
          }
        }
      };

      input.click();
    }
  }

  // Front Page & Navigation Methods
  private setupFrontPageControls() {
    const levelPuzzlesBtn = document.getElementById('level-puzzles-btn');
    const dailyPuzzleBtn = document.getElementById('daily-puzzle-btn');
    const sharedPuzzlesBtn = document.getElementById('shared-puzzles-btn');
    const loginBtn = document.getElementById('login-btn');
    const backToMenuBtn = document.getElementById('back-to-menu');
    const closeLevelModalBtn = document.getElementById('close-level-modal');
    const prevLevelPageBtn = document.getElementById('prev-level-page-btn');
    const nextLevelPageBtn = document.getElementById('next-level-page-btn');
    const closeCalendarBtn = document.getElementById('close-calendar');
    const nextPuzzleBtn = document.getElementById('next-puzzle-btn');
    const remixPuzzleBtn = document.getElementById('remix-puzzle-btn');
    const prevMonthBtn = document.getElementById('prev-month-btn');
    const nextMonthBtn = document.getElementById('next-month-btn');

    if (levelPuzzlesBtn) {
      const showLevels = () => {
        console.log('Level puzzles button clicked');
        const completedLevels = DailyPuzzleManager.getCompletedLevels();
        let nextLevel = 1;
        while (completedLevels.includes(`level_${nextLevel}`)) {
          nextLevel++;
        }
        this.currentLevelPage = Math.floor((nextLevel - 1) / LEVELS_PER_PAGE);
        this.showLevelModal();
      };
      levelPuzzlesBtn.addEventListener('click', showLevels);
      levelPuzzlesBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        showLevels();
      });
    }

    if (closeLevelModalBtn) {
      this.addClickHandler(closeLevelModalBtn as HTMLElement, () => {
        this.hideLevelModal();
      });
    }

    if (prevLevelPageBtn) {
      const prevPage = () => {
        if (this.currentLevelPage > 0) {
          this.currentLevelPage--;
          this.renderLevelGrid();
        }
      };
      prevLevelPageBtn.addEventListener('click', prevPage);
      prevLevelPageBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        prevPage();
      });
    }

    if (nextLevelPageBtn) {
      const nextPage = () => {
        this.currentLevelPage++;
        this.renderLevelGrid();
      };
      nextLevelPageBtn.addEventListener('click', nextPage);
      nextLevelPageBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        nextPage();
      });
    }

    if (dailyPuzzleBtn) {
      const showCalendar = () => {
        console.log('Daily puzzle button clicked');
        this.showCalendarModal();
      };
      dailyPuzzleBtn.addEventListener('click', showCalendar);
      dailyPuzzleBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        showCalendar();
      });
    }

    if (sharedPuzzlesBtn) {
      const showCollections = () => {
        console.log('Shared puzzles button clicked');
        this.showCollectionsModal();
      };
      sharedPuzzlesBtn.addEventListener('click', showCollections);
      sharedPuzzlesBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        showCollections();
      });
    }

    if (loginBtn) {
      // Hide login button on web platform
      if (PLATFORM_TYPE === 'web') {
        loginBtn.style.display = 'none';
      } else {
        const openLogin = async () => {
          console.log('Login button clicked');
          try {
            await this.performLogin();
          } catch (e: any) {
            if (e?.message !== 'USER_CANCELLED') {
              console.error('Login failed:', e);
            }
          }
        };
        loginBtn.addEventListener('click', openLogin);
        loginBtn.addEventListener('touchend', (e) => {
          e.preventDefault();
          openLogin();
        });
      }
    }

    if (backToMenuBtn) {
      this.addClickHandler(backToMenuBtn as HTMLElement, () => {
        const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();

        // If we're in a level puzzle, go back to the level modal
        if (currentPuzzle?.type === 'level') {
          const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.fillStyle = '#1a1a1a';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
          }
          const gameView = document.getElementById('game-view');
          if (gameView) gameView.style.display = 'none';
          this.showLevelModal();
        }
        // If we're in a daily puzzle, go back to the calendar
        else if (currentPuzzle?.type === 'daily') {
          // Clear the canvas before going back
          const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.fillStyle = '#1a1a1a';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
          }

          const gameView = document.getElementById('game-view');
          if (gameView) gameView.style.display = 'none';
          this.showCalendarModal();
        }
        // If we're in a shared puzzle, go back to the collection
        else if (currentPuzzle?.type === 'shared' && this.currentCollectionId) {
          this.showGameView(); // Ensure game view is hidden
          const frontPage = document.getElementById('front-page');
          const gameView = document.getElementById('game-view');
          if (frontPage) frontPage.style.display = 'none';
          if (gameView) gameView.style.display = 'none';

          // Show the collection detail modal
          this.showCollectionDetailModal(this.currentCollectionId);
        } else {
          // Otherwise go back to front page
          this.showFrontPage();
        }
      });
    }

    if (closeCalendarBtn) {
      this.addClickHandler(closeCalendarBtn as HTMLElement, () => {
        this.hideCalendarModal();
      });
    }

    if (nextPuzzleBtn) {
      this.addClickHandler(nextPuzzleBtn as HTMLElement, async () => {
        const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();

        if (currentPuzzle?.type === 'shared') {
          // Load next shared puzzle in collection
          this.loadNextSharedPuzzle();
        } else if (currentPuzzle?.type === 'level') {
          const nextNum = this.getNextLevelNum();
          if (nextNum !== null) {
            this.updateNextPuzzleButton(false); // hide immediately to prevent double-tap
            await this.loadLevelPuzzle(nextNum);
          } else {
            this.showFrontPage();
            this.showLevelModal();
          }
        } else if (currentPuzzle?.type === 'daily') {
          // Load next daily puzzle
          const nextPuzzle = this.getNextUncompletedPuzzle();
          if (nextPuzzle) {
            await this.loadDailyPuzzle(nextPuzzle.date);
          } else {
            // No more daily puzzles available
            alert('No more daily puzzles to do until tomorrow. Great work!');
            this.showFrontPage();
          }
        }
      });
    }

    if (remixPuzzleBtn) {
      this.addClickHandler(remixPuzzleBtn as HTMLElement, async () => {
        // Reshuffle the current puzzle
        if (this.game) {
          const randomSeed = (Math.floor(Math.random() * 2147483646) + 1); // non-zero 31-bit int
          this.game.shufflePieces(randomSeed);
          // Hide the remix button after reshuffling
          this.updateRemixPuzzleButton(false);
        }
      });
    }

    if (prevMonthBtn) {
      this.addClickHandler(prevMonthBtn as HTMLElement, () => {
        this.changeCalendarMonth(-1);
      });
    }

    if (nextMonthBtn) {
      this.addClickHandler(nextMonthBtn as HTMLElement, () => {
        this.changeCalendarMonth(1);
      });
    }

    // Swipe gestures for level modal
    const levelModalContent = document.getElementById('level-modal-content');
    if (levelModalContent) {
      this.addSwipeHandler(levelModalContent,
        () => { // swipe left = next page (only if all current page puzzles are done)
          const nextBtn = document.getElementById('next-level-page-btn') as HTMLElement;
          if (!nextBtn || nextBtn.style.visibility !== 'visible') return;
          const inner = document.getElementById('level-content-inner');
          if (inner) {
            this.animateSlide(inner, 'left', () => {
              this.currentLevelPage++;
              this.renderLevelGrid();
            });
          }
        },
        () => { // swipe right = prev page, or back if on first page
          if (this.currentLevelPage > 0) {
            const inner = document.getElementById('level-content-inner');
            if (inner) {
              this.animateSlide(inner, 'right', () => {
                this.currentLevelPage--;
                this.renderLevelGrid();
              });
            }
          } else {
            this.hideLevelModal();
          }
        }
      );
    }

    // Swipe gestures for calendar modal
    const calendarModalContent = document.getElementById('calendar-modal-content');
    if (calendarModalContent) {
      this.addSwipeHandler(calendarModalContent,
        () => { // swipe left = next month (only if not already at current month)
          const today = new Date();
          const isCurrentMonth = this.currentCalendarMonth === today.getMonth() + 1
            && this.currentCalendarYear === today.getFullYear();
          if (!isCurrentMonth) {
            const inner = document.getElementById('calendar-content-inner');
            if (inner) this.animateSlide(inner, 'left', () => this.changeCalendarMonth(1));
          }
        },
        () => { // swipe right = prev month, or back if at minimum month
          const isAtMinMonth = this.currentCalendarYear === 2025 && this.currentCalendarMonth === 10;
          if (!isAtMinMonth) {
            const inner = document.getElementById('calendar-content-inner');
            if (inner) this.animateSlide(inner, 'right', () => this.changeCalendarMonth(-1));
          } else {
            this.hideCalendarModal();
          }
        }
      );
    }

    // Collections Modal controls
    const closeCollectionsBtn = document.getElementById('close-collections');
    const createCollectionBtn = document.getElementById('create-collection-btn');
    const closeCollectionDetailBtn = document.getElementById('close-collection-detail');
    const addPuzzleToCollectionBtn = document.getElementById('add-puzzle-to-collection-btn');
    const saveCollectionBtn = document.getElementById('save-collection-btn');
    const cancelCollectionBtn = document.getElementById('cancel-collection-btn');

    if (closeCollectionsBtn) {
      this.addClickHandler(closeCollectionsBtn as HTMLElement, () => {
        this.hideCollectionsModal();
      });
    }

    if (createCollectionBtn) {
      this.addClickHandler(createCollectionBtn as HTMLElement, () => {
        this.showCreateCollectionModal();
      });
    }

    if (closeCollectionDetailBtn) {
      this.addClickHandler(closeCollectionDetailBtn as HTMLElement, () => {
        // Make sure we show the front page first
        const frontPage = document.getElementById('front-page');
        const gameView = document.getElementById('game-view');
        if (frontPage) frontPage.style.display = 'flex';
        if (gameView) gameView.style.display = 'none';

        this.hideCollectionDetailModal();
        this.showCollectionsModal();
      });
    }

    if (addPuzzleToCollectionBtn) {
      this.addClickHandler(addPuzzleToCollectionBtn as HTMLElement, async () => {
        await this.addPhotosToCollection();
      });
    }

    if (saveCollectionBtn) {
      this.addClickHandler(saveCollectionBtn as HTMLElement, () => {
        this.createNewCollection();
      });
    }

    if (cancelCollectionBtn) {
      this.addClickHandler(cancelCollectionBtn as HTMLElement, () => {
        this.hideCreateCollectionModal();
      });
    }

    // Settings Modal controls
    const settingsBtn = document.getElementById('settings-btn');
    const closeSettingsBtn = document.getElementById('close-settings-btn');
    const clearProgressBtn = document.getElementById('clear-progress-btn');
    const clearAllDataBtn = document.getElementById('clear-all-data-btn');

    if (settingsBtn) {
      this.addClickHandler(settingsBtn as HTMLElement, () => {
        this.showSettingsModal();
      });
    }

    if (closeSettingsBtn) {
      this.addClickHandler(closeSettingsBtn as HTMLElement, () => {
        this.hideSettingsModal();
      });
    }

    if (clearProgressBtn) {
      this.addClickHandler(clearProgressBtn as HTMLElement, () => {
        this.clearProgress();
      });
    }

    if (clearAllDataBtn) {
      this.addClickHandler(clearAllDataBtn as HTMLElement, () => {
        this.clearAllData();
      });
    }

    const loginLogoutBtn = document.getElementById('login-logout-btn');
    if (loginLogoutBtn) {
      this.addClickHandler(loginLogoutBtn as HTMLElement, async () => {
        await this.handleLoginLogout();
      });
    }
  }

  private async preloadFrontPageImages() {
    const completedLevels = DailyPuzzleManager.getCompletedLevels();
    let nextLevel = 1;
    while (completedLevels.includes(`level_${nextLevel}`)) {
      nextLevel++;
    }

    const [dailyResult, levelResult] = await Promise.allSettled([
      getTodaysPuzzle(),
      getLevelPuzzleByNum(nextLevel),
    ]);

    const preload = (url: string) => { new Image().src = url; };

    if (dailyResult.status === 'fulfilled' && dailyResult.value?.imageUrl) {
      preload(dailyResult.value.imageUrl);
    }
    if (levelResult.status === 'fulfilled' && levelResult.value?.imageUrl) {
      preload(levelResult.value.imageUrl);
    }
  }

  private showFrontPage() {
    const frontPage = document.getElementById('front-page');
    const gameView = document.getElementById('game-view');

    if (frontPage) frontPage.style.display = 'flex';
    if (gameView) gameView.style.display = 'none';
  }

  private showGameView() {
    const frontPage = document.getElementById('front-page');
    const gameView = document.getElementById('game-view');

    if (frontPage) frontPage.style.display = 'none';
    if (gameView) gameView.style.display = 'flex';

    // Force canvas resize now that game view is visible
    if (this.game) {
      // Use setTimeout to ensure the layout has been applied
      setTimeout(() => {
        this.game?.handleResize();
      }, 0);
    }
  }

  private showCalendarModal() {
    const modal = document.getElementById('calendar-modal');
    if (modal) {
      // Force a fresh read of completed puzzles to ensure localStorage is synced
      console.log('Opening calendar modal');

      // Set button visibility before rendering
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();
      const isCurrentMonth = this.currentCalendarMonth === currentMonth && this.currentCalendarYear === currentYear;

      // Check if we're already at the minimum month (October 2025)
      const isAtMinMonth = this.currentCalendarYear === 2025 && this.currentCalendarMonth === 10;
      const hasPreviousMonthPuzzles = !isAtMinMonth;

      const nextMonthBtn = document.getElementById('next-month-btn');
      const prevMonthBtn = document.getElementById('prev-month-btn');
      if (nextMonthBtn) {
        nextMonthBtn.style.visibility = isCurrentMonth ? 'hidden' : 'visible';
      }
      if (prevMonthBtn) {
        prevMonthBtn.style.visibility = hasPreviousMonthPuzzles ? 'visible' : 'hidden';
      }

      this.renderCalendar();
      this.applyLazyBackground(modal as HTMLElement);
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideCalendarModal(showFrontPage: boolean = true) {
    const modal = document.getElementById('calendar-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
    // Show front page when closing calendar (unless loading a puzzle)
    if (showFrontPage) {
      this.showFrontPage();
    }
  }

  private showLevelModal() {
    const modal = document.getElementById('level-modal');
    if (modal) {
      this.renderLevelGrid();
      this.applyLazyBackground(modal as HTMLElement);
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private applyLazyBackground(el: HTMLElement) {
    if (!el.style.backgroundImage && el.dataset.lazyBg) {
      el.style.backgroundImage = `url('${el.dataset.lazyBg}')`;
    }
  }

  private hideLevelModal(showFrontPage: boolean = true) {
    const modal = document.getElementById('level-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
    if (showFrontPage) {
      this.showFrontPage();
    }
  }

  private renderLevelGrid() {
    const grid = document.getElementById('level-grid');
    if (!grid) return;

    const completedLevels = DailyPuzzleManager.getCompletedLevels();
    const startNum = this.currentLevelPage * LEVELS_PER_PAGE + 1;
    const endNum = (this.currentLevelPage + 1) * LEVELS_PER_PAGE;

    // Update title
    const titleEl = document.getElementById('level-page-title');
    if (titleEl) titleEl.textContent = `Level Puzzles ${startNum}-${endNum}`;

    // Show prev button if not on first page
    const prevBtn = document.getElementById('prev-level-page-btn') as HTMLElement;
    if (prevBtn) prevBtn.style.visibility = this.currentLevelPage > 0 ? 'visible' : 'hidden';

    // Show next button only if all levels on current page are completed
    const allCompleted = Array.from({ length: LEVELS_PER_PAGE }, (_, i) => `level_${startNum + i}`)
      .every(id => completedLevels.includes(id));
    const nextBtn = document.getElementById('next-level-page-btn') as HTMLElement;
    if (nextBtn) nextBtn.style.visibility = allCompleted ? 'visible' : 'hidden';

    const checkmarkSvg = `<svg class="level-cell-crown" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 1px 3px rgba(0,0,0,0.8));">
      <path d="M20 6L9 17L4 12" stroke="#86efac" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    let html = '';
    for (let num = startNum; num <= endNum; num++) {
      const id = `level_${num}`;
      const isCompleted = completedLevels.includes(id);
      // Available if first on this page or the previous level is completed
      const isAvailable = num === startNum || completedLevels.includes(`level_${num - 1}`);
      const isLocked = !isAvailable && !isCompleted;

      let classes = 'level-cell';
      if (isCompleted) classes += ' completed';
      if (isLocked) classes += ' locked';

      let thumbStyle = '';
      if (isCompleted) {
        const thumb = DailyPuzzleManager.getLevelThumbnail(id);
        if (thumb) {
          thumbStyle = `background-image: url('${thumb}'); background-size: cover; background-position: center;`;
        }
      }

      html += `<div class="${classes}" data-level="${num}" style="${thumbStyle}">
        ${isCompleted ? checkmarkSvg : `<span class="level-cell-number">${num}</span>`}
      </div>`;
    }

    grid.innerHTML = html;

    // Wire up click handlers
    grid.querySelectorAll('.level-cell:not(.locked)').forEach(cell => {
      const num = parseInt(cell.getAttribute('data-level') || '0', 10);
      if (!num) return;
      this.addClickHandler(cell as HTMLElement, async () => {
        (cell as HTMLElement).classList.add('loading');
        await this.loadLevelPuzzle(num);
      });
    });
  }

  async loadLevelPuzzle(num: number) {
    const puzzle = await getLevelPuzzleByNum(num);
    if (!puzzle) {
      alert(`No puzzle available for level ${num}. Please try again.`);
      return;
    }

    this.currentLevelNum = num;
    this.currentPuzzleServerId = null;
    this.currentDailyPuzzleDate = null;

    DailyPuzzleManager.setCurrentPuzzle({ type: 'level', id: `level_${num}` });

    const isCompleted = DailyPuzzleManager.isLevelCompleted(`level_${num}`);

    this.hideLevelModal(false);

    const gameHeader = document.querySelector('#game-header h1');
    if (gameHeader) {
      gameHeader.textContent = `Puzzle #${num}`;
    }

    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    this.showGameView();

    const loading = document.getElementById('loading');
    if (loading) loading.style.display = 'block';

    if (this.game) {
      const gridSize = FORCE_3X3_GRID ? 3 : puzzle.difficulty;
      this.game.setGridSize(gridSize);

      const gridSizeSelectFooter = document.getElementById('grid-size-footer') as HTMLSelectElement;
      if (gridSizeSelectFooter) gridSizeSelectFooter.value = String(gridSize);

      this.game.setOnCompleteCallback(() => {
        console.log('Level puzzle completed:', num);
        DailyPuzzleManager.markLevelCompleted(`level_${num}`);
        // Capture a small thumbnail from the puzzle source image
        const thumbImg = new Image();
        thumbImg.crossOrigin = 'anonymous';
        thumbImg.onload = () => {
          const size = 80;
          const srcW = thumbImg.naturalWidth;
          const srcH = thumbImg.naturalHeight;
          // Crop the centre square
          const cropSize = Math.min(srcW, srcH);
          const srcX = (srcW - cropSize) / 2;
          const srcY = (srcH - cropSize) / 2;
          const thumbCanvas = document.createElement('canvas');
          thumbCanvas.width = size;
          thumbCanvas.height = size;
          const tCtx = thumbCanvas.getContext('2d');
          if (tCtx) {
            tCtx.drawImage(thumbImg, srcX, srcY, cropSize, cropSize, 0, 0, size, size);
            const dataUrl = thumbCanvas.toDataURL('image/jpeg', 0.6);
            DailyPuzzleManager.saveLevelThumbnail(`level_${num}`, dataUrl);
          }
        };
        thumbImg.src = puzzle.imageUrl;
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButton(true);
      });

      try {
        await this.game.initialize(puzzle.imageUrl, !isCompleted, puzzle.seed || 0);
      } catch (error) {
        console.error('Failed to load level puzzle image:', error);
        alert('Failed to load the puzzle image. Please check your connection and try again.');
        if (loading) loading.style.display = 'none';
        this.showFrontPage();
        this.showLevelModal();
        return;
      }

      if (loading) loading.style.display = 'none';

      if (isCompleted) {
        this.updateRemixPuzzleButton(true);
        this.updateNextPuzzleButton(false);
      } else {
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButton(false);
      }
    }
  }

  private getNextLevelNum(): number | null {
    if (this.currentLevelNum === null) return null;
    const next = this.currentLevelNum + 1;
    const pageEnd = (this.currentLevelPage + 1) * LEVELS_PER_PAGE;
    if (next > pageEnd) return null;
    // Only offer the next level if it hasn't been completed yet
    if (DailyPuzzleManager.isLevelCompleted(`level_${next}`)) return null;
    return next;
  }

  private changeCalendarMonth(delta: number) {
    // Calculate new month/year
    let newMonth = this.currentCalendarMonth + delta;
    let newYear = this.currentCalendarYear;

    // Wrap around months
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }

    // Calculate button visibility for the NEW month BEFORE rendering
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const isCurrentMonth = newMonth === currentMonth && newYear === currentYear;

    // Check if we're at the minimum month (October 2025)
    const isAtMinMonth = newYear === 2025 && newMonth === 10;
    const hasPreviousMonthPuzzles = !isAtMinMonth;

    // Set button visibility BEFORE updating month variables
    const nextMonthBtn = document.getElementById('next-month-btn');
    const prevMonthBtn = document.getElementById('prev-month-btn');
    if (nextMonthBtn) {
      nextMonthBtn.style.visibility = isCurrentMonth ? 'hidden' : 'visible';
    }
    if (prevMonthBtn) {
      prevMonthBtn.style.visibility = hasPreviousMonthPuzzles ? 'visible' : 'hidden';
    }

    // Force a reflow to apply button visibility immediately
    if (nextMonthBtn) nextMonthBtn.offsetHeight;
    if (prevMonthBtn) prevMonthBtn.offsetHeight;

    // Now update the month/year
    this.currentCalendarMonth = newMonth;
    this.currentCalendarYear = newYear;

    // Render the calendar
    this.renderCalendar();
  }

  private renderCalendar() {
    const grid = document.getElementById('calendar-grid');
    const monthTitle = document.getElementById('calendar-month-title');
    if (!grid) return;

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const completedPuzzles = DailyPuzzleManager.getCompletedPuzzles();
    const today = new Date();

    // Update month title
    if (monthTitle) {
      monthTitle.textContent = `${monthNames[this.currentCalendarMonth - 1]} ${this.currentCalendarYear}`;
    }

    // Add day headers
    let html = days.map(day =>
      `<div class="calendar-day-header">${day}</div>`
    ).join('');

    // Get first day of month and number of days
    const firstDay = new Date(this.currentCalendarYear, this.currentCalendarMonth - 1, 1);
    const firstDayOffset = firstDay.getDay();
    const daysInMonth = new Date(this.currentCalendarYear, this.currentCalendarMonth, 0).getDate();

    // Add empty cells for days before month start
    for (let i = 0; i < firstDayOffset; i++) {
      html += '<div class="calendar-day disabled"></div>';
    }

    // Get today's date string for comparison
    const todayString = today.toISOString().split('T')[0];

    // Add days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${this.currentCalendarYear}-${String(this.currentCalendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      // Check if completed by constructing the expected puzzle ID
      const expectedPuzzleId = `daily_${dateString}`;
      const isCompleted = completedPuzzles.includes(expectedPuzzleId);

      const isAvailable = DailyPuzzleManager.isDateAvailable(dateString);
      const isFuture = !isAvailable;
      const isToday = dateString === todayString;

      let classes = 'calendar-day';
      // Only mark as future if the date is actually in the future
      if (isFuture) classes += ' future';
      if (isCompleted) classes += ' completed';
      if (isToday) classes += ' today';

      const checkmarkSvg = `<svg class="calendar-day-crown" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17L4 12" stroke="#86efac" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

      html += `
        <div class="${classes}" data-date="${dateString}">
          ${isCompleted ? checkmarkSvg : `<span class="calendar-day-number">${day}</span>`}
        </div>
      `;
    }

    grid.innerHTML = html;

    // Add touch event listeners to calendar days
    const calendarDays = grid.querySelectorAll('.calendar-day');
    calendarDays.forEach(day => {
      const dateString = day.getAttribute('data-date');
      if (!dateString) return;

      const isAvailable = DailyPuzzleManager.isDateAvailable(dateString);

      // Make dates clickable if they're available (past or today)
      // The API will be checked when the puzzle is loaded
      if (isAvailable) {
        this.addClickHandler(day as HTMLElement, async () => {
          // Add loading state to clicked day
          (day as HTMLElement).classList.add('loading');

          // On native platforms, load directly. On web, use URL navigation for proper routing
          if (Capacitor.isNativePlatform()) {
            await this.loadDailyPuzzle(dateString);
          } else {
            // Web: navigate to /daily/YYYY-MM-DD URL
            window.location.href = `/daily/${dateString}`;
          }
        });
      }
    });
  }

  private getNextUncompletedPuzzle(): DailyPuzzle | null {
    const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();
    if (!currentPuzzle || currentPuzzle.type !== 'daily') {
      return null;
    }

    const completedPuzzles = DailyPuzzleManager.getCompletedPuzzles();

    // Get current puzzle date (IDs use format "daily_2025-11-12")
    const currentDateString = currentPuzzle.id.replace('daily_', '');
    const currentDate = new Date(currentDateString);

    // Check if today's puzzle is available and not completed
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];
    const todayPuzzleId = `daily_${todayString}`;

    if (currentDateString !== todayString && !completedPuzzles.includes(todayPuzzleId)) {
      // Suggest today's puzzle if we're not already on it
      return {
        id: todayPuzzleId,
        date: todayString,
        imageUrl: '', // Will be loaded from API
        difficulty: 3,
        seed: 0
      };
    }

    // Try next day after current puzzle
    const nextDate = new Date(currentDate);
    nextDate.setDate(nextDate.getDate() + 1);
    const nextDateString = nextDate.toISOString().split('T')[0];
    const nextPuzzleId = `daily_${nextDateString}`;

    // Only suggest next day if it's not in the future and not completed
    if (DailyPuzzleManager.isDateAvailable(nextDateString) && !completedPuzzles.includes(nextPuzzleId)) {
      return {
        id: nextPuzzleId,
        date: nextDateString,
        imageUrl: '', // Will be loaded from API
        difficulty: 3,
        seed: 0
      };
    }

    return null;
  }

  private updateNextPuzzleButton(showAfterCompletion: boolean = false) {
    const nextPuzzleBtn = document.getElementById('next-puzzle-btn');
    if (!nextPuzzleBtn) return;

    const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();

    // Only show button after completion
    if (!currentPuzzle || !showAfterCompletion) {
      nextPuzzleBtn.style.display = 'none';
      return;
    }

    if (currentPuzzle.type === 'level') {
      nextPuzzleBtn.style.display = this.getNextLevelNum() !== null ? 'block' : 'none';
    } else if (currentPuzzle.type === 'daily') {
      const nextPuzzle = this.getNextUncompletedPuzzle();
      nextPuzzleBtn.style.display = nextPuzzle ? 'block' : 'none';
    } else {
      nextPuzzleBtn.style.display = 'none';
    }
  }

  private updateRemixPuzzleButton(show: boolean) {
    const remixPuzzleBtn = document.getElementById('remix-puzzle-btn');
    if (!remixPuzzleBtn) return;

    remixPuzzleBtn.style.display = show ? 'block' : 'none';
  }

  async loadDailyPuzzle(dateString: string) {
    const puzzle = await getDailyPuzzleByDate(dateString);
    if (!puzzle) {
      console.error('Puzzle not found for date:', dateString);
      alert('No puzzle available for this date. Please try another date.');
      return;
    }

    // Validate puzzle has required imageUrl
    if (!puzzle.imageUrl) {
      console.error('Puzzle missing imageUrl:', puzzle);
      alert('This puzzle is missing image data. Please try another date.');
      return;
    }

    // Set the daily puzzle date for sharing
    this.currentPuzzleServerId = null;
    this.currentDailyPuzzleDate = dateString;

    // Set current puzzle
    DailyPuzzleManager.setCurrentPuzzle({
      type: 'daily',
      id: puzzle.id
    });

    // Check if this puzzle was already completed
    const isCompleted = DailyPuzzleManager.isPuzzleCompleted(puzzle.id);

    // Hide calendar without showing front page (we're loading the puzzle)
    this.hideCalendarModal(false);

    // Update the game header title
    const gameHeader = document.querySelector('#game-header h1');
    if (gameHeader) {
      gameHeader.textContent = dateString;
    }

    // Clear the canvas immediately and show game view
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Clear the entire canvas to remove old puzzle
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    // Show game view (needed for canvas rendering)
    this.showGameView();

    // Show loading indicator
    const loading = document.getElementById('loading');
    if (loading) {
      loading.style.display = 'block';
    }

    // Set grid size and load image
    if (this.game) {
      const gridSize = FORCE_3X3_GRID ? 3 : puzzle.difficulty;
      this.game.setGridSize(gridSize);

      // Update grid size select (footer only)
      const gridSizeSelectFooter = document.getElementById('grid-size-footer') as HTMLSelectElement;
      if (gridSizeSelectFooter) gridSizeSelectFooter.value = String(gridSize);

      // Set completion callback for daily puzzles
      this.game.setOnCompleteCallback(() => {
        console.log('Daily puzzle completed:', puzzle.id);
        DailyPuzzleManager.markPuzzleCompleted(puzzle.id);
        // Hide remix button and show next puzzle button after completion
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButton(true);
      });

      // Load the puzzle image - don't shuffle if already completed
      try {
        await this.game.initialize(puzzle.imageUrl, !isCompleted, puzzle.seed || 0);
      } catch (error) {
        console.error('Failed to load daily puzzle image:', error);
        alert('Failed to load the puzzle image. Please check your connection and try again.');

        // Navigate back to the calendar view
        if (loading) {
          loading.style.display = 'none';
        }
        this.showFrontPage();
        this.showCalendarModal();
        return;
      }

      // Hide loading indicator
      if (loading) {
        loading.style.display = 'none';
      }

      // If puzzle was already completed, show remix button instead of next button
      if (isCompleted) {
        this.updateRemixPuzzleButton(true);
        this.updateNextPuzzleButton(false);
      } else {
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButton(false);
      }
    }
  }

  // Collections Methods
  private showCollectionsModal() {
    const modal = document.getElementById('collections-modal');
    if (modal) {
      this.renderCollectionsGrid();
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideCollectionsModal() {
    const modal = document.getElementById('collections-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
  }

  private renderCollectionsGrid() {
    const grid = document.getElementById('collections-grid');
    if (!grid) return;

    const collections = CollectionManager.getCollections();

    if (collections.length === 0) {
      grid.innerHTML = '<div style="grid-column: 1 / -1; text-align: center; color: rgba(255, 255, 255, 0.6); padding: 40px;">No collections yet. Create your first one!</div>';
      return;
    }

    grid.innerHTML = collections.map(collection => {
      const puzzleCount = collection.puzzles.length;

      // Find the first completed puzzle in the collection
      const completedPuzzle = collection.puzzles.find(p =>
        DailyPuzzleManager.isSharedPuzzleCompleted(p.id)
      );

      // If there's a completed puzzle, show its thumbnail with overlay
      // Otherwise, show just the name and count
      const cardContent = completedPuzzle
        ? `
          <div style="aspect-ratio: 1; position: relative; overflow: hidden;">
            <img src="${completedPuzzle.imageUrl}" style="width: 100%; height: 100%; object-fit: cover;">
            <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.6) 100%); display: flex; flex-direction: column; align-items: center; justify-content: center;">
              <div style="font-weight: 700; font-size: 20px; margin-bottom: 8px; text-shadow: 2px 2px 4px rgba(0,0,0,0.8);">${collection.name}</div>
              <div style="font-size: 14px; opacity: 0.9; text-shadow: 1px 1px 2px rgba(0,0,0,0.8);">${puzzleCount} puzzle${puzzleCount !== 1 ? 's' : ''}</div>
            </div>
          </div>
        `
        : `
          <div style="aspect-ratio: 1; background: linear-gradient(135deg, #22c55e 0%, #15803d 100%); display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 20px;">
            <div style="font-weight: 700; font-size: 20px; margin-bottom: 8px; text-align: center;">${collection.name}</div>
            <div style="font-size: 14px; opacity: 0.9;">${puzzleCount} puzzle${puzzleCount !== 1 ? 's' : ''}</div>
          </div>
        `;

      return `
        <div class="collection-item" data-collection-id="${collection.id}" style="position: relative; cursor: pointer; border-radius: 8px; overflow: hidden; background: rgba(255, 255, 255, 0.1); transition: all 0.2s ease;">
          ${cardContent}
        </div>
      `;
    }).join('');

    // Add click handlers
    const collectionItems = grid.querySelectorAll('.collection-item');
    collectionItems.forEach(item => {
      const handleClick = () => {
        const collectionId = item.getAttribute('data-collection-id');
        if (collectionId) {
          this.showCollectionDetailModal(collectionId);
        }
      };

      this.addClickHandler(item as HTMLElement, handleClick);

      // Add hover effect
      item.addEventListener('mouseenter', () => {
        (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.2)';
        (item as HTMLElement).style.transform = 'translateY(-2px)';
      });

      item.addEventListener('mouseleave', () => {
        (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.1)';
        (item as HTMLElement).style.transform = 'translateY(0)';
      });
    });
  }

  private showCollectionDetailModal(collectionId: string) {
    this.currentCollectionId = collectionId;
    const modal = document.getElementById('collection-detail-modal');
    const title = document.getElementById('collection-detail-title');

    if (modal && title) {
      const collection = CollectionManager.getCollectionById(collectionId);
      if (!collection) return;

      title.textContent = collection.name;
      this.renderCollectionPuzzles(collection);
      this.hideCollectionsModal();
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideCollectionDetailModal(clearCollectionId: boolean = true) {
    const modal = document.getElementById('collection-detail-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
      // Only clear collection ID if explicitly requested (e.g., going back to menu)
      // Don't clear it when loading a puzzle, so we can show Next button
      if (clearCollectionId) {
        this.currentCollectionId = null;
      }
    }
  }

  private renderCollectionPuzzles(collection: PuzzleCollection) {
    const grid = document.getElementById('collection-puzzles-grid');
    const emptyMessage = document.getElementById('collection-empty');
    if (!grid || !emptyMessage) return;

    if (collection.puzzles.length === 0) {
      grid.innerHTML = '';
      emptyMessage.style.display = 'block';
      return;
    }

    emptyMessage.style.display = 'none';

    const greyGradient = 'linear-gradient(135deg, #636363 0%, #a2a2a2 100%)';

    grid.innerHTML = collection.puzzles.map((puzzle, index) => {
      const isCompleted = DailyPuzzleManager.isSharedPuzzleCompleted(puzzle.id);
      const imageContent = isCompleted
        ? `<img src="${puzzle.imageUrl}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null; this.parentElement.innerHTML='<div style=\\'width: 100%; height: 100%; background: ${greyGradient}; display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 48px; color: rgba(255,255,255,0.7);\\'>⚠<div style=\\'font-size: 14px; margin-top: 10px;\\'>Photo deleted</div></div>'; this.parentElement.parentElement.setAttribute('data-broken', 'true');">`
        : `<div style="width: 100%; height: 100%; background: ${greyGradient}; display: flex; align-items: center; justify-content: center; font-size: 80px; font-weight: bold; color: #15803d; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);">?</div>`;

      return `
        <div class="puzzle-item" data-puzzle-id="${puzzle.id}" data-puzzle-index="${index}" style="position: relative; cursor: pointer; border-radius: 8px; overflow: hidden; background: rgba(255, 255, 255, 0.1); transition: all 0.2s ease;">
          <div style="aspect-ratio: 1; overflow: hidden;">
            ${imageContent}
          </div>
          <div style="padding: 8px; text-align: center;">
            <div style="font-size: 14px; font-weight: 600; margin-bottom: 4px;">${puzzle.title}</div>
            <div style="font-size: 12px; opacity: 0.8;">${FORCE_3X3_GRID ? '3x3' : puzzle.difficulty + 'x' + puzzle.difficulty}</div>
          </div>
          ${!collection.isDefault ? `<button class="puzzle-delete-btn" data-puzzle-id="${puzzle.id}" style="position: absolute; top: 5px; right: 5px; background: rgba(255, 0, 0, 0.8); color: white; border: none; border-radius: 50%; width: 30px; height: 30px; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.2s;">×</button>` : ''}
        </div>
      `;
    }).join('');

    // Add click handlers for puzzles
    const puzzleItems = grid.querySelectorAll('.puzzle-item');
    puzzleItems.forEach(item => {
      const handlePuzzleClick = (e?: Event) => {
        const target = e?.target as HTMLElement;
        // Don't trigger if clicking delete button
        if (target && target.classList.contains('puzzle-delete-btn')) return;

        const puzzleId = item.getAttribute('data-puzzle-id');
        const puzzleIndex = parseInt(item.getAttribute('data-puzzle-index') || '0');
        if (puzzleId) {
          this.loadSharedPuzzle(puzzleId, puzzleIndex);
        }
      };

      this.addClickHandler(item as HTMLElement, handlePuzzleClick);

      // Add hover effects for desktop
      item.addEventListener('mouseenter', () => {
        (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.2)';
        (item as HTMLElement).style.transform = 'translateY(-2px)';
        const deleteBtn = item.querySelector('.puzzle-delete-btn') as HTMLElement;
        if (deleteBtn) deleteBtn.style.opacity = '1';
      });

      item.addEventListener('mouseleave', () => {
        (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.1)';
        (item as HTMLElement).style.transform = 'translateY(0)';
        const deleteBtn = item.querySelector('.puzzle-delete-btn') as HTMLElement;
        if (deleteBtn) deleteBtn.style.opacity = '0';
      });

      // Add long press for mobile to show delete button
      if (Capacitor.isNativePlatform()) {
        let longPressTimer: number | null = null;
        let touchStartX = 0;
        let touchStartY = 0;
        const deleteBtn = item.querySelector('.puzzle-delete-btn') as HTMLElement;

        item.addEventListener('touchstart', (e) => {
          const touchEvent = e as TouchEvent;
          touchStartX = touchEvent.touches[0].clientX;
          touchStartY = touchEvent.touches[0].clientY;

          // Start long press timer (500ms)
          longPressTimer = window.setTimeout(() => {
            // Show delete button on long press
            if (deleteBtn) {
              deleteBtn.style.opacity = '1';
              // Add slight vibration feedback if available
              if ('vibrate' in navigator) {
                navigator.vibrate(50);
              }
            }
          }, 500);
        }, { passive: true });

        item.addEventListener('touchmove', (e) => {
          // Cancel long press if finger moves too much
          const touchEvent = e as TouchEvent;
          const touchX = touchEvent.touches[0].clientX;
          const touchY = touchEvent.touches[0].clientY;
          const deltaX = Math.abs(touchX - touchStartX);
          const deltaY = Math.abs(touchY - touchStartY);

          if (deltaX > 10 || deltaY > 10) {
            if (longPressTimer !== null) {
              window.clearTimeout(longPressTimer);
              longPressTimer = null;
            }
          }
        }, { passive: true });

        item.addEventListener('touchend', () => {
          // Cancel long press timer if touch ends before timeout
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
        });

        item.addEventListener('touchcancel', () => {
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
        });
      }
    });

    // Add delete handlers
    const deleteButtons = grid.querySelectorAll('.puzzle-delete-btn');
    deleteButtons.forEach(btn => {
      const handleDelete = (e?: Event) => {
        e?.stopPropagation();
        const puzzleId = btn.getAttribute('data-puzzle-id');
        if (puzzleId && confirm('Delete this puzzle?')) {
          CollectionManager.deletePuzzleFromCollection(collection.id, puzzleId);

          // Refresh the view
          const updatedCollection = CollectionManager.getCollectionById(collection.id);
          if (updatedCollection) {
            this.renderCollectionPuzzles(updatedCollection);
          } else {
            // Collection was deleted (empty)
            this.hideCollectionDetailModal();
            this.showCollectionsModal();
          }
        }
      };

      this.addClickHandler(btn as HTMLElement, handleDelete);
    });

    // On mobile, hide all delete buttons when tapping on the grid background
    if (Capacitor.isNativePlatform() && grid) {
      grid.addEventListener('touchstart', (e) => {
        // Only hide if tapping the grid itself, not a puzzle item or delete button
        const target = e.target as HTMLElement;
        if (target.id === 'collection-puzzles-grid') {
          const allDeleteBtns = grid.querySelectorAll('.puzzle-delete-btn') as NodeListOf<HTMLElement>;
          allDeleteBtns.forEach(btn => {
            btn.style.opacity = '0';
          });
        }
      }, { passive: true });
    }
  }

  private showCreateCollectionModal() {
    const modal = document.getElementById('create-collection-modal');
    const input = document.getElementById('collection-name-input') as HTMLInputElement;

    if (modal && input) {
      input.value = '';
      (modal as HTMLElement).style.display = 'flex';
      setTimeout(() => input.focus(), 100);
    }
  }

  private hideCreateCollectionModal() {
    const modal = document.getElementById('create-collection-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
  }

  private createNewCollection() {
    const input = document.getElementById('collection-name-input') as HTMLInputElement;
    const name = input.value.trim();

    if (!name) {
      alert('Please enter a collection name');
      return;
    }

    const collection = CollectionManager.createCollection(name);
    if (!collection) {
      alert('Failed to create collection. Storage may be full.');
      return;
    }

    this.hideCreateCollectionModal();
    this.renderCollectionsGrid();
  }

  private showPuzzleDetailsModal(thumbnailDataUrl: string, defaultTitle: string, current: number, total: number): Promise<{ title: string; difficulty: number } | null> {
    return new Promise((resolve) => {
      const modal = document.getElementById('puzzle-details-modal') as HTMLElement;
      const thumbnail = document.getElementById('puzzle-details-thumbnail') as HTMLImageElement;
      const titleInput = document.getElementById('puzzle-details-title') as HTMLInputElement;
      const progressEl = document.getElementById('puzzle-details-progress') as HTMLElement;
      const addBtn = document.getElementById('puzzle-details-add') as HTMLElement;
      const skipBtn = document.getElementById('puzzle-details-skip') as HTMLElement;
      const difficultyBtns = modal.querySelectorAll('.difficulty-btn') as NodeListOf<HTMLElement>;

      thumbnail.src = thumbnailDataUrl;
      thumbnail.style.display = 'block';
      titleInput.value = defaultTitle;

      if (total > 1) {
        progressEl.textContent = `Image ${current} of ${total}`;
        progressEl.style.display = 'block';
      } else {
        progressEl.style.display = 'none';
      }

      let selectedDifficulty = 4;
      const updateDifficultyUI = () => {
        difficultyBtns.forEach(btn => {
          const d = parseInt(btn.getAttribute('data-difficulty') || '4');
          btn.style.outline = d === selectedDifficulty ? '2px solid white' : 'none';
          btn.style.opacity = d === selectedDifficulty ? '1' : '0.55';
        });
      };
      updateDifficultyUI();

      // Reuse app-wide tap handler logic (needed because global touch-action:none suppresses click on iOS)
      type Cleanup = () => void;
      const cleanups: Cleanup[] = [];
      const addTapHandler = (el: HTMLElement, fn: () => void) => {
        let startX = 0, startY = 0, startTime = 0;
        const onTouchStart = (e: TouchEvent) => { startX = e.touches[0].clientX; startY = e.touches[0].clientY; startTime = Date.now(); };
        const onTouchEnd = (e: TouchEvent) => {
          if (Math.abs(e.changedTouches[0].clientX - startX) < 10 && Math.abs(e.changedTouches[0].clientY - startY) < 10 && Date.now() - startTime < 500) {
            e.preventDefault();
            fn();
          }
        };
        el.addEventListener('click', fn);
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchend', onTouchEnd);
        cleanups.push(() => { el.removeEventListener('click', fn); el.removeEventListener('touchstart', onTouchStart); el.removeEventListener('touchend', onTouchEnd); });
      };

      const cleanup = () => {
        modal.style.display = 'none';
        cleanups.forEach(fn => fn());
      };

      difficultyBtns.forEach(btn => {
        addTapHandler(btn, () => {
          selectedDifficulty = parseInt(btn.getAttribute('data-difficulty') || '4');
          updateDifficultyUI();
        });
      });

      addTapHandler(addBtn, () => {
        cleanup();
        resolve({ title: titleInput.value.trim() || defaultTitle, difficulty: selectedDifficulty });
      });
      addTapHandler(skipBtn, () => {
        cleanup();
        resolve(null);
      });

      modal.style.display = 'flex';
      setTimeout(() => titleInput.focus(), 100);
    });
  }

  private async uploadPhotoToCloud(compressedDataUrl: string, title: string, difficulty: number): Promise<{ serverId: string; imageUrl: string }> {
    const token = localStorage.getItem('joinsaw_auth_token');
    if (!token) throw new Error('Not logged in');

    const blob = dataUrlToBlob(compressedDataUrl);
    const formData = new FormData();
    formData.append('image', blob, 'photo.webp');
    formData.append('json', JSON.stringify({ title, difficulty }));

    const uploadResponse = await fetch(`${BASE_URL}/Tools/upload?login_auth=${token}`, {
      method: 'POST',
      body: formData,
    });

    if (!uploadResponse.ok) throw new Error(`Upload failed: ${uploadResponse.status}`);
    const uploadData = await uploadResponse.json();
    if (uploadData.status !== 'ok') throw new Error(uploadData.msg || 'Upload failed');

    const serverId: string = uploadData.id;

    const puzzleResponse = await fetch(`${BASE_URL}/Rest/puzzle/${serverId}`);
    if (!puzzleResponse.ok) throw new Error(`Failed to fetch puzzle: ${puzzleResponse.status}`);
    const puzzleData = await puzzleResponse.json();
    const puzzle = puzzleData.result || puzzleData;
    if (!puzzle.imageUrl) throw new Error('Server puzzle missing imageUrl');

    return { serverId, imageUrl: puzzle.imageUrl };
  }

  private async addPhotosToCollection() {
    if (!this.currentCollectionId) return;

    console.log('Add photos to collection:', this.currentCollectionId);

    // Check login first
    const token = localStorage.getItem('joinsaw_auth_token');
    if (!token) {
      sessionStorage.setItem('joinsaw_pending_upload', 'true');
      sessionStorage.setItem('joinsaw_pending_upload_collection', this.currentCollectionId);
      try {
        await this.performLogin();
      } catch (e: any) {
        if (e?.message !== 'USER_CANCELLED') {
          console.error('Login failed:', e);
        }
        sessionStorage.removeItem('joinsaw_pending_upload');
        sessionStorage.removeItem('joinsaw_pending_upload_collection');
      }
      return;
    }

    const showSpinner = () => {
      const el = document.getElementById('upload-spinner');
      if (el) el.style.display = 'flex';
    };
    const hideSpinner = () => {
      const el = document.getElementById('upload-spinner');
      if (el) el.style.display = 'none';
    };

    if (Capacitor.isNativePlatform()) {
      try {
        const images = await Camera.pickImages({ quality: 90, limit: 10 });

        let processedCount = 0;
        let failedCount = 0;
        let skippedCount = 0;

        for (const image of images.photos) {
          const webPath = image.webPath;
          if (!webPath) continue;
          try {
            const compressedDataUrl = await ImageCompressor.compress(webPath, 1200, 1200, 0.85);
            const imageNum = processedCount + failedCount + skippedCount + 1;
            const details = await this.showPuzzleDetailsModal(compressedDataUrl, `Puzzle ${imageNum}`, imageNum, images.photos.length);
            if (!details) { skippedCount++; continue; }

            const spinnerTimer = setTimeout(showSpinner, 1000);
            try {
              const { serverId, imageUrl } = await this.uploadPhotoToCloud(compressedDataUrl, details.title, details.difficulty);
              const puzzle = CollectionManager.addPuzzleToCollection(this.currentCollectionId, imageUrl, details.title, details.difficulty, serverId);
              if (puzzle) processedCount++;
              else failedCount++;
            } finally {
              clearTimeout(spinnerTimer);
              hideSpinner();
            }
          } catch (error) {
            console.error('Failed to upload photo:', error);
            failedCount++;
          }
        }

        if (processedCount === 0 && failedCount > 0) {
          alert('Failed to add puzzles. Please check your connection and try again.');
        } else if (failedCount > 0) {
          alert(`Added ${processedCount} puzzle(s). ${failedCount} failed.`);
        }

        const collection = CollectionManager.getCollectionById(this.currentCollectionId);
        if (collection) this.renderCollectionPuzzles(collection);
      } catch (error) {
        console.error('Error picking photo', error);
      }
    } else {
      // Browser fallback
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;

      input.onchange = async (e) => {
        const files = (e.target as HTMLInputElement).files;
        if (!files || files.length === 0 || !this.currentCollectionId) return;

        let processedCount = 0;
        let failedCount = 0;
        let skippedCount = 0;

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          try {
            const dataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = e => resolve(e.target?.result as string);
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });
            const compressedDataUrl = await ImageCompressor.compress(dataUrl, 1200, 1200, 0.85);
            const defaultTitle = file.name.replace(/\.[^/.]+$/, '');
            const imageNum = processedCount + failedCount + skippedCount + 1;
            const details = await this.showPuzzleDetailsModal(compressedDataUrl, defaultTitle, imageNum, files.length);
            if (!details) { skippedCount++; continue; }

            const spinnerTimer = setTimeout(showSpinner, 1000);
            try {
              const { serverId, imageUrl } = await this.uploadPhotoToCloud(compressedDataUrl, details.title, details.difficulty);
              const puzzle = CollectionManager.addPuzzleToCollection(this.currentCollectionId!, imageUrl, details.title, details.difficulty, serverId);
              if (puzzle) processedCount++;
              else failedCount++;
            } finally {
              clearTimeout(spinnerTimer);
              hideSpinner();
            }
          } catch (error) {
            console.error('Failed to upload photo:', error);
            failedCount++;
          }
        }

        if (processedCount === 0 && failedCount > 0) {
          alert('Failed to add puzzles. Please check your connection and try again.');
        } else if (failedCount > 0) {
          alert(`Added ${processedCount} puzzle(s). ${failedCount} failed.`);
        }

        const collection = CollectionManager.getCollectionById(this.currentCollectionId!);
        if (collection) this.renderCollectionPuzzles(collection);
      };

      input.click();
    }
  }

  async loadEmbeddedPuzzle(p: EmbeddedPuzzleData, dailyDate: string | null, serverId: string | null) {
    this.showGameView();
    const loading = document.getElementById('loading');
    if (loading) loading.style.display = 'block';

    try {
      this.currentDailyPuzzleDate = dailyDate;
      this.currentPuzzleServerId = serverId;

      if (p.type === 'daily' && dailyDate) {
        DailyPuzzleManager.setCurrentPuzzle({ type: 'daily', id: p.id });
        const isCompleted = DailyPuzzleManager.isPuzzleCompleted(p.id);
        const gameHeader = document.querySelector('#game-header h1');
        if (gameHeader) gameHeader.textContent = dailyDate;
        if (this.game) {
          this.game.setGridSize(FORCE_3X3_GRID ? 3 : p.difficulty);
          this.game.setOnCompleteCallback(() => {
            DailyPuzzleManager.markPuzzleCompleted(p.id);
            this.updateRemixPuzzleButton(false);
            this.updateNextPuzzleButton(true);
          });
          await this.game.initialize(p.imageUrl, !isCompleted, p.seed || 0);
        }
      } else if (this.game) {
        this.game.setGridSize(p.difficulty || 4);
        await this.game.initialize(p.imageUrl, true, p.seed || 0);
      }
    } catch (error) {
      console.error('Failed to load embedded puzzle:', error);
      alert('Failed to load the puzzle. Please check your connection and try again.');
    } finally {
      if (loading) loading.style.display = 'none';
    }
  }

  async loadSharedPuzzleFromServer(serverId: string) {
    this.showGameView();
    const loading = document.getElementById('loading');
    if (loading) loading.style.display = 'block';

    try {
      const response = await fetch(`${BASE_URL}/Rest/puzzle/${serverId}`);
      if (!response.ok) throw new Error(`Failed to fetch puzzle: ${response.status}`);
      const data = await response.json();
      const p = data.result || data;

      this.currentPuzzleServerId = serverId;
      this.currentDailyPuzzleDate = null;

      if (this.game) {
        this.game.setGridSize(p.difficulty || 4);
        await this.game.initialize(p.imageUrl, true, p.seed || 0);
      }
    } catch (error) {
      console.error('Failed to load shared puzzle:', error);
      alert('Failed to load the puzzle. Please check your connection and try again.');
    } finally {
      if (loading) loading.style.display = 'none';
    }
  }

  async loadSharedPuzzle(puzzleId: string, puzzleIndex: number = 0) {
    const puzzle = CollectionManager.getPuzzleById(puzzleId);
    if (!puzzle) {
      console.error('Puzzle not found:', puzzleId);
      return;
    }

    // Store the current puzzle index and server ID for sharing
    this.currentPuzzleIndex = puzzleIndex;
    this.currentPuzzleServerId = puzzle.serverId || null;
    this.currentDailyPuzzleDate = null; // Clear daily puzzle date

    // Set current puzzle
    DailyPuzzleManager.setCurrentPuzzle({
      type: 'shared',
      id: puzzle.id
    });

    // Check if this puzzle was already completed
    const isCompleted = DailyPuzzleManager.isSharedPuzzleCompleted(puzzle.id);

    // Hide collection detail modal and show game view
    // Don't clear collection ID so we can show Next button
    this.hideCollectionDetailModal(false);
    this.showGameView();

    // Clear canvas and show loading indicator
    const loading = document.getElementById('loading');
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Fill with background color instead of just clearing to avoid visual artifacts
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    if (loading) {
      loading.style.display = 'block';
    }

    // Set grid size
    if (this.game) {
      const gridSize = FORCE_3X3_GRID ? 3 : puzzle.difficulty;
      this.game.setGridSize(gridSize);

      // Update grid size select (footer only)
      const gridSizeSelectFooter = document.getElementById('grid-size-footer') as HTMLSelectElement;
      if (gridSizeSelectFooter) gridSizeSelectFooter.value = String(gridSize);

      // Set completion callback for shared puzzles
      this.game.setOnCompleteCallback(() => {
        console.log('Shared puzzle completed:', puzzle.id);
        DailyPuzzleManager.markSharedPuzzleCompleted(puzzle.id);
        // Hide remix button and show next puzzle button after completion
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButtonForShared(true);
      });

      // Load the puzzle image - don't shuffle if already completed
      try {
        await this.game.initialize(puzzle.imageUrl, !isCompleted, puzzle.seed || 0);
      } catch (error) {
        console.error('Failed to load puzzle image:', error);
        if (loading) loading.style.display = 'none';
        this.showFrontPage();

        const remove = confirm('This puzzle image could not be loaded. Remove it from your collection?');
        if (remove) {
          CollectionManager.deletePuzzleFromCollection(puzzle.collectionId, puzzle.id);
        }

        if (this.currentCollectionId) {
          this.showCollectionDetailModal(this.currentCollectionId);
        } else {
          this.showCollectionsModal();
        }
        return;
      }

      // Hide loading indicator
      if (loading) {
        loading.style.display = 'none';
      }

      // If puzzle was already completed, show remix button
      if (isCompleted) {
        this.updateRemixPuzzleButton(true);
        this.updateNextPuzzleButtonForShared(false);
      } else {
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButtonForShared(false);
      }
    }
  }

  private updateNextPuzzleButtonForShared(showAfterCompletion: boolean = false) {
    console.log('updateNextPuzzleButtonForShared called, showAfterCompletion:', showAfterCompletion);
    const nextPuzzleBtn = document.getElementById('next-puzzle-btn');
    if (!nextPuzzleBtn) {
      console.log('Next puzzle button not found');
      return;
    }

    const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();
    console.log('Current puzzle:', currentPuzzle);

    // Only show button for shared puzzles and after completion
    if (!currentPuzzle || currentPuzzle.type !== 'shared' || !showAfterCompletion) {
      console.log('Hiding button - not shared or not after completion');
      nextPuzzleBtn.style.display = 'none';
      return;
    }

    // Check if there's a next puzzle in the collection
    if (!this.currentCollectionId) {
      console.log('Hiding button - no current collection ID');
      nextPuzzleBtn.style.display = 'none';
      return;
    }

    const collection = CollectionManager.getCollectionById(this.currentCollectionId);
    console.log('Collection:', collection, 'Current index:', this.currentPuzzleIndex);

    if (!collection || this.currentPuzzleIndex >= collection.puzzles.length - 1) {
      console.log('Hiding button - no collection or last puzzle');
      nextPuzzleBtn.style.display = 'none';
      return;
    }

    // There is a next puzzle, show the button
    console.log('Showing next puzzle button!');
    nextPuzzleBtn.style.display = 'block';
    nextPuzzleBtn.textContent = 'Next Puzzle';
  }

  private loadNextSharedPuzzle() {
    if (!this.currentCollectionId) return;

    const collection = CollectionManager.getCollectionById(this.currentCollectionId);
    if (!collection) return;

    const nextIndex = this.currentPuzzleIndex + 1;
    if (nextIndex >= collection.puzzles.length) return;

    const nextPuzzle = collection.puzzles[nextIndex];
    this.loadSharedPuzzle(nextPuzzle.id, nextIndex);
  }

  // Settings Methods
  private showSettingsModal() {
    const modal = document.getElementById('settings-modal');
    if (modal) {
      this.updateLoginLogoutButton();
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideSettingsModal() {
    const modal = document.getElementById('settings-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
  }

  private clearProgress() {
    const confirmed = confirm('Are you sure you want to clear your puzzle completion progress? Your collections and photos will remain.');

    if (!confirmed) {
      return;
    }

    // Clear only completion progress data
    localStorage.removeItem('joinsaw_daily_completed');
    localStorage.removeItem('joinsaw_shared_completed');
    localStorage.removeItem('joinsaw_level_completed');

    // Clear level thumbnails
    const thumbKeys = Object.keys(localStorage).filter(k => k.startsWith('joinsaw_level_thumb_'));
    thumbKeys.forEach(k => localStorage.removeItem(k));

    // Close settings modal
    this.hideSettingsModal();

    // Show success message
    alert('Puzzle completion progress has been cleared. The app will reload.');

    // Reload the page to refresh the UI
    window.location.reload();
  }

  private clearAllData() {
    const confirmed = confirm('Are you sure you want to clear all data? This will delete all completed puzzles, collections, and photos. This action cannot be undone.');

    if (!confirmed) {
      return;
    }

    // Clear all localStorage data
    const keysToRemove = [
      'joinsaw_daily_completed',
      'joinsaw_shared_completed',
      'joinsaw_current_puzzle',
      'joinsaw_collections',
      'joinsaw_photos'
    ];

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    // Reinitialize default collection
    CollectionManager.getCollections();

    // Close settings modal
    this.hideSettingsModal();

    // Show success message
    alert('All data has been cleared successfully. The app will reload.');

    // Reload the page to reset everything
    window.location.reload();
  }

  // Deep Link Handler
  private async handleDeepLink(url: string) {
    console.log('Handling deep link:', url);

    try {
      const urlObj = new URL(url);
      const path = urlObj.hostname; // e.g., 'login' from joinsaw://login
      const params = new URLSearchParams(urlObj.search);

      // Handle login deep link: joinsaw://login?state=xxx&login_auth=yyy
      if (path === 'login') {
        const state = params.get('state');
        const loginAuth = params.get('login_auth');

        // Verify state matches what we stored (prevents malicious deep links)
        const storedState = sessionStorage.getItem('joinsaw_login_state');

        if (!state || state !== storedState) {
          console.error('Invalid state parameter - possible security attack');
          alert('Login failed: Invalid security token. Please try again.');
          return;
        }

        // Clear the state (one-time use only)
        sessionStorage.removeItem('joinsaw_login_state');

        if (loginAuth) {
          console.log('Received login authentication:', loginAuth);
          // Store the auth token
          localStorage.setItem('joinsaw_auth_token', loginAuth);

          await Browser.close();
          this.updateLoginLogoutButton();

          const pendingUploadCollection = sessionStorage.getItem('joinsaw_pending_upload_collection');
          if (sessionStorage.getItem('joinsaw_pending_upload') && pendingUploadCollection) {
            sessionStorage.removeItem('joinsaw_pending_upload');
            sessionStorage.removeItem('joinsaw_pending_upload_collection');
            this.currentCollectionId = pendingUploadCollection;
            this.showCollectionDetailModal(pendingUploadCollection);
            await this.addPhotosToCollection();
          } else {
            this.showFrontPage();
          }
        } else {
          console.warn('Login deep link missing login_auth parameter');
          alert('Login failed: Missing authentication token.');
        }
      } else if (path === 'logout') {
        // Handle logout deep link: joinsaw://logout?state=xxx
        const state = params.get('state');

        // Verify state matches what we stored (prevents malicious deep links)
        const storedState = sessionStorage.getItem('joinsaw_logout_state');

        if (!state || state !== storedState) {
          console.error('Invalid state parameter - possible security attack');
          alert('Logout failed: Invalid security token.');
          return;
        }

        // Clear the state (one-time use only)
        sessionStorage.removeItem('joinsaw_logout_state');

        console.log('Logout confirmed via deep link');
        await Browser.close();
        this.updateLoginLogoutButton();
        this.showFrontPage();
      } else if (path === 'daily') {
        const date = urlObj.pathname.replace(/^\//, '');
        if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          await this.loadDailyPuzzle(date);
        } else {
          console.warn('Invalid date in daily deep link:', date);
        }
      } else if (path === 'puzzle') {
        const serverId = urlObj.pathname.replace(/^\//, '');
        if (serverId) {
          await this.loadSharedPuzzleFromServer(serverId);
        }
      } else {
        console.warn('Unknown deep link path:', path);
      }
    } catch (error) {
      console.error('Error parsing deep link URL:', error);
    }
  }

  // Sign in with Apple natively and exchange the identity token for a login_auth token
  private async appleSignIn(): Promise<void> {
    const result = await SignInWithApple.authorize({
      clientId: 'com.joinsaw.app',
      redirectURI: BASE_URL,
      scopes: 'email name',
    });
    const { identityToken, givenName, familyName } = result.response;

    const params = new URLSearchParams({ identityToken });
    if (givenName) params.set('firstName', givenName);
    if (familyName) params.set('lastName', familyName);

    const response = await fetch(`${BASE_URL}/Ajax/appleLogin?${params.toString()}`);
    if (!response.ok) throw new Error('Server error during Apple login');
    const data = await response.json();

    if (data.login_auth) {
      localStorage.setItem('joinsaw_auth_token', data.login_auth);
      this.updateLoginLogoutButton();
    } else {
      throw new Error(data.error || 'Apple login failed');
    }
  }

  // Open login flow appropriate for the current platform
  private async performLogin(): Promise<void> {
    if (Capacitor.getPlatform() === 'ios') {
      await this.appleSignIn();
    } else {
      const state = this.generateRandomState();
      sessionStorage.setItem('joinsaw_login_state', state);
      await Browser.open({ url: `${BASE_URL}/Account/login?successUri=app&state=${state}` });
    }
  }

  // Generate random state for CSRF protection
  private generateRandomState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  // Update login/logout button state based on authentication
  private updateLoginLogoutButton() {
    const section = document.getElementById('login-logout-section');
    const btn = document.getElementById('login-logout-btn');
    const description = document.getElementById('login-logout-description');

    if (!section || !btn || !description) return;

    // Only show on native platform
    if (PLATFORM_TYPE !== 'app') {
      section.style.display = 'none';
      return;
    }

    const authToken = localStorage.getItem('joinsaw_auth_token');
    const isLoggedIn = !!authToken;

    section.style.display = 'block';

    if (isLoggedIn) {
      btn.textContent = 'Logout';
      description.textContent = 'Sign out of your account.';
      btn.style.background = 'rgba(239, 68, 68, 0.8)';
      btn.style.borderColor = 'rgba(239, 68, 68, 1)';
    } else {
      btn.textContent = 'Login';
      description.textContent = 'Sign in to sync your progress.';
      btn.style.background = 'rgba(34, 197, 94, 0.8)';
      btn.style.borderColor = 'rgba(34, 197, 94, 1)';
    }
  }

  // Handle login/logout button click
  private async handleLoginLogout() {
    const authToken = localStorage.getItem('joinsaw_auth_token');
    const isLoggedIn = !!authToken;

    if (isLoggedIn) {
      // Handle logout
      const confirmed = confirm('Are you sure you want to logout?');
      if (!confirmed) return;

      // Generate random state and store it for verification
      const state = this.generateRandomState();
      sessionStorage.setItem('joinsaw_logout_state', state);

      // Clear the auth token
      localStorage.removeItem('joinsaw_auth_token');

      // Navigate to logout URL
      await Browser.open({ url: `${BASE_URL}/Account/logout?successUri=app&state=${state}` });

      // Close settings modal and update UI
      this.hideSettingsModal();
    } else {
      // Handle login
      try {
        await this.performLogin();
        this.hideSettingsModal();
      } catch (e: any) {
        if (e?.message !== 'USER_CANCELLED') {
          console.error('Login failed:', e);
          alert('Sign in failed. Please try again.');
        }
      }
    }
  }
}

// Make app instance available globally for onclick handlers
declare global {
  interface Window {
    app: App;
  }
}

const app = new App();
window.app = app;
app.initialize().catch(console.error);
